import { useState, useCallback } from 'react';
import { Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChantiersSidebar } from './ChantiersSidebar';

interface ChantierSelectorOverlayProps {
  chantiers: Array<{
    id: string;
    nom_chantier: string;
    client: string;
    statut: string;
    etat_chantier: string;
    rentabilite?: number;
    categorie?: string;
    date_debut_prevue?: string;
    date_fin_estimee?: string;
    pictogramme?: string;
    couleur?: string;
  }>;
  onSelectChantier: (id: string) => void;
}

/**
 * Overlay centré de sélection de chantier.
 * Affiché quand l'utilisateur arrive sur /financial sans chantier pré-sélectionné.
 * Réutilise ChantiersSidebar en version élargie avec animation de sortie
 * qui slide vers la position du panneau replié (bord droit).
 */
const ChantierSelectorOverlay = ({ chantiers, onSelectChantier }: ChantierSelectorOverlayProps) => {
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const handleSelect = useCallback((id: string) => {
    setIsAnimatingOut(true);
    // Attendre la fin de l'animation avant de notifier le parent
    setTimeout(() => {
      onSelectChantier(id);
    }, 550);
  }, [onSelectChantier]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center p-4">
      {/* Backdrop sombre */}
      <div
        className={cn(
          'absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-[600ms]',
          isAnimatingOut ? 'opacity-0' : 'opacity-100'
        )}
      />

      {/* Conteneur animé — slide vers la position du collapsed tab (right-0, mid-screen) */}
      <div
        className={cn(
          'relative z-10 max-w-md w-full flex flex-col gap-4',
          'transition-[transform,opacity] duration-[600ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
          isAnimatingOut
            ? 'scale-x-[0.2] scale-y-[0.5] translate-x-[calc(50vw-45px)] opacity-0'
            : 'scale-100 opacity-100 translate-x-0 animate-scale-in'
        )}
      >
        {/* Titre — fade-out rapide pour ne pas gêner l'animation du panneau */}
        <div className={cn(
          "text-center transition-opacity duration-200",
          isAnimatingOut && "opacity-0"
        )}>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-card/90 rounded-xl border border-border/50 backdrop-blur-md shadow-lg mb-3">
            <Building2 className="w-5 h-5 text-primary" />
            <span className="text-lg font-bold text-foreground">
              Gestion financière
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Sélectionnez un chantier pour accéder à sa gestion financière
          </p>
        </div>

        {/* Sidebar réutilisée en carte centrée */}
        <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden max-h-[70vh] [&>aside]:w-full [&>aside]:border-0 [&>aside]:rounded-none">
          <ChantiersSidebar
            selectedChantierId=""
            onSelectChantier={handleSelect}
            chantiers={chantiers}
            loading={false}
          />
        </div>
      </div>
    </div>
  );
};

export default ChantierSelectorOverlay;
